import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as behaviorAPI from '../../../../actions/behavior';
import * as courseRegistrationAPI from '../../../../actions/registration/course';
import * as courseLandingPageAPI from '../../../../actions/content/courseLandingPages';
import Icon, { IconTypes } from '../../../common/Icon';
import config from '../../../../config/config';
import stateConfig from '../../../../config/state';
import urls from '../../../../config/urls';
import validator from '../../../../utils/validator';

function EventCard({ displayFavorite, displayProgress, favorites, watched, eventData, user, behaviorAPI, courseRegistration, courseLandingPage, courseRegistrationAPI, courseLandingPageAPI }) {
  const [isFavorite, setIsFavorite] = useState(null);
  const [percentWatched, setPercentWatched] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [loadingCourseLandingPage, setLoadingCourseLandingPage] = useState(false);
  const [hasCourseLandingPage, setHasCourseLandingPage] = useState(null);
  const stateId = `${stateConfig.keys.COURSE_LANDING_PAGE}`;
  const navigate = useNavigate();
  // const rating = null; // not currently being used
  // defining fns used in useEffect blocks
  let initialize = config.emptyFn;
  let checkIsFavorite = config.emptyFn;

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    const favorite = checkIsFavorite();
    if(favorite !== isFavorite) {
      setIsFavorite(favorite);
    }
  }, [favorites, eventData, isFavorite, checkIsFavorite]);

  const getUserRoute = useCallback((eventData) => {
    const { slug, type, status, linkedList, course } = eventData;

    /* 
      LCS Program 'List Card' Exception - For event card(s) asw 'Rediscovering Advent':
      - Student who has NOT applied yet -> user click should navigate to Rediscovering Advent Course Landing Page
      - Student who HAS applied already -> user click should navigate to Rediscovering Advent Event/Course Details Page
      (Event Details Page -> While 'Upcoming', Course Details Page -> When 'Running Now')
      
      List tag for Rediscovering Advent is 'Rediscovering Advent' -> slug: 'rediscovering-advent'
    */
    const rediscoveringAdventTagRegex = new RegExp('rediscovering-advent', 'g');
    if(linkedList !== null && !alreadyApplied) {
      if(validator.matchesPattern(linkedList.slug, rediscoveringAdventTagRegex)) {
        return urls.rediscoveringAdventLandingPage; 
      }
    } else if (linkedList !== null && alreadyApplied) {
      if(validator.matchesPattern(linkedList.slug, rediscoveringAdventTagRegex)) {
        return urls.rediscoveringAdventEventPage; 
      }
    }

    if (status === 'Upcoming' && type === "Live Course" && !alreadyApplied && eventData.magdala === false && hasCourseLandingPage) {
      return `/landing/${slug}`;
    }

    if (status === 'Upcoming' && type === 'Live Course' && !alreadyApplied && eventData.magdala === false && !hasCourseLandingPage) {
      return urls.notFound;
    }

    if(status !== 'Upcoming' && (type === 'Live Course' || type === 'Self-Paced Course')) {
      if(course && course.releaseDate) {
        return `/events/${slug}`;
      } else {
        return `/courses/${slug}`;
      }
    } else {
      return `/events/${slug}`;
    }
  }, [alreadyApplied, hasCourseLandingPage])

  useEffect(() => {
    if(loadingCourseLandingPage && hasCourseLandingPage === null && courseLandingPage.ids) {
      const { slug } = eventData;
      const rObj = courseLandingPage.byId[`${stateId}.${slug}`];

      if(rObj) {
        if(!rObj || rObj.error) {
          setLoadingCourseLandingPage(false);
          setHasCourseLandingPage(false);
        } else {
          setLoadingCourseLandingPage(false);
          setHasCourseLandingPage(true);
        }
      } else {
        return
      }
    }
  }, [hasCourseLandingPage, courseLandingPage.ids, courseLandingPage.byId, eventData, loadingCourseLandingPage, stateId]);

  useEffect(() => {
    if(loading && !Boolean(registration) && courseRegistration.ids) {
      const { slug } = eventData;
      const rObj = courseRegistration.byId[slug];

      if(rObj) {
        if(!rObj || rObj.error) {
          setLoading(false);
          setAlreadyApplied(false);
          setRegistration(rObj);
        } else {
          setLoading(false);
          setAlreadyApplied(true);
          setRegistration(rObj);
        }
      }
    }
  }, [loading, registration, eventData, courseRegistration.ids, courseRegistration.byId]);

  useEffect(() => {
    if ( loading || !validator.isDefined(registration) || loadingCourseLandingPage || !validator.isDefined(hasCourseLandingPage)) return;
    const route = getUserRoute(eventData);
    navigate(route);

  }, [ eventData, navigate, registration, getUserRoute, loading, hasCourseLandingPage, loadingCourseLandingPage])

  const onClick = () => {
    const { status, type, magdala } = eventData;
    if (status === 'Upcoming' && (type === 'Live Course' || type === 'Lecture List') && magdala === false) {
      retrieveCourseRegistration();
      retrieveCourseLandingPage();
    } else {
      const route = getUserRoute(eventData);
      navigate(route);
    }
  }
  
  const retrieveCourseRegistration = () => {
    const { slug } = eventData;
    const { byId } = courseRegistration;
    const rObj = byId[slug];

    if(rObj && !validator.isDefined(rObj.error)) {
      setLoading(false);
      setAlreadyApplied(true);
      setRegistration(rObj);
    } else {
      courseRegistrationAPI.getCourseRegistrationStatus(slug);
      setLoading(true);
    }
  }

  const retrieveCourseLandingPage = () => {
    const { slug, status } = eventData;
    const { byId } = courseLandingPage;
    const rObj = byId[`${stateId}.${slug}`];

    if(rObj && !validator.isDefined(rObj.error)) {
      setLoadingCourseLandingPage(false);
      setHasCourseLandingPage(true);
      return;
    }
    if(status === "Upcoming") {
      courseLandingPageAPI.getCourseLandingPageBySlug(slug);
      setLoadingCourseLandingPage(true);
    } else {
      setLoadingCourseLandingPage(false);
      setHasCourseLandingPage(false);
    }
  }

  initialize = () => {
    setIsFavorite(checkIsFavorite());
    const log = watched.find(log => log.eventId === eventData.id);
    if(log && log.progress < 100) {
      return setPercentWatched(log.progress);
    }
  };

  checkIsFavorite = () => {
    const index = favorites.findIndex(item => item === eventData.id);
    return index !== -1;
  };

  const onClickFavorite = (event) => {
    event.stopPropagation();
    if(isFavorite) {
      behaviorAPI.removeFavorite(eventData.id);
      setIsFavorite(false);
    } else {
      behaviorAPI.addFavorite(eventData.id);
      setIsFavorite(true);
    }
  }

  const determineTypeDisplay = () => {
    const { type } = eventData;
    switch(type) {
      case 'Course List':
        return 'Courses';
      case 'Lecture List':
        return 'Lectures';
      default:
        return type;
    }
  }
  
  const { thumbnail: eventThumb, title, subtitle, status, date, instructor, linkedList } = eventData;
  const thumbnail = eventThumb || config.eventDefaultFeatureImage;
  const isLinkedListCard = linkedList !== null;

  return (
    <div className="EventCard" onClick={onClick}>
      <div className="feature" style={{backgroundImage: `url(${thumbnail})`}}>
        <div className="overlay"></div>
        <div className="actions">
          { displayFavorite && !validator.isEmpty(user.email) &&
            <Fragment>
              { isFavorite? 
                <div className="favorite is-favorite" onClick={onClickFavorite}><Icon type={IconTypes.FavoriteFilled} /></div> :
                <div className="favorite not-favorite" onClick={onClickFavorite}><Icon type={IconTypes.Favorite} /></div> }
            </Fragment>
          }
        </div>
        <div className="progress-bar">
          { displayProgress && percentWatched !== null && !(percentWatched === 0) &&
            <Fragment>
              <div className="background"></div>
              <div className="progress" style={{width: `${percentWatched}%`}}></div>
            </Fragment>
          }
        </div>
        <div className="info-bar">
          { //Not currently being used
          /*displayRating && rating !== null && !(rating === 0) && 
            <div className="rating"><Icon type={IconTypes.RatingFilled} /> {rating}</div>
          */
          }
          <p className="type">{determineTypeDisplay()}</p>
        </div>
      </div>
      <div className="description">
        <p className="title">{title}</p>
        <p className="subtitle">{subtitle}</p>
        <p className="instructor">{isLinkedListCard? linkedList.description : instructor.name}</p>
        <div className="meta-tags">
          { status === 'Running Now' &&
            <div className="highlight"><Icon type={IconTypes.EventRunning} inline={true} /> {status}</div> 
          }
          { status === 'Upcoming' && !isLinkedListCard &&
            <div><Icon type={IconTypes.Events} inline={true} /> &nbsp;{date}</div> 
          }
        </div>
      </div>
    </div>
  );
}

EventCard.defaultProps = {
  displayFavorite: false,
  displayRating: false,
  displayProgress: false
};

EventCard.propTypes = {
  eventData: PropTypes.object
};

function mapStateToProps(state) {
  const { favorites, watched } = state.behavior;
  return { favorites, watched, user: state.user, courseRegistration: state.courseRegistration, courseLandingPage: state.courseLandingPage };
}

function mapDispatchToProps(dispatch) {
  return { 
    behaviorAPI: bindActionCreators(behaviorAPI, dispatch),
    courseRegistrationAPI: bindActionCreators(courseRegistrationAPI, dispatch),
    courseLandingPageAPI: bindActionCreators(courseLandingPageAPI, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(EventCard);